import { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import agentApiService from '../services/agentApiService';

/**
 * Transform backend SearchHotelDetailsResponseDto to frontend expected structure
 */
const transformHotelDetailsResponse = (backendData) => {
  if (!backendData) {
    console.error('❌ transformHotelDetailsResponse: backendData is null or undefined');
    return null;
  }

  // Transform rooms data
  const transformedRooms = backendData.rooms?.map(room => ({
    roomNames: room.roomCategory || [],
    roomBookingCode: room.roomBookingCode,
    roomImages: room.roomImages || [],
    roomPrice: room.totalFare,
    totalFare: room.totalFare,
    crossOutPrice: null, // Not in backend response
    // Platform fee and GST fields
    platformFee: room.platFormFee,
    gstPercentageOnPlatformFee: room.gstPercentageOnPlatFormFee,
    gstAmountOnPlatformFee: room.gstAmountOnPlatFormFee,
    impDetails: {
      mealType: room.mealType,
      isRefundable: room.isRefundable,
      withTransfers: room.withTransfer,
      payAt: null, // Can be derived from payAtHotel if needed
    },
    roomDescription: room.roomDescription,
    roomFacilities: room.roomFacilities || [],
    inclusion: room.inclusion || [],
    roomPromotion: room.roomPromotion || [],
    cancellationPolicies: room.cancellationPolicy?.map(policy => ({
      startDate: policy.startDate,
      endDate: policy.endDate,
      chargeType: policy.chargeType,
      chargeAmount: policy.chargeAmount,
    })) || [],
    payAtHotel: room.payAtHotel || [],
  })) || [];

  // Build overview object
  const overview = {
    hotelName: backendData.hotelName,
    rating: backendData.hotelStar,
    ratingWord: backendData.hotelStar >= 4 ? 'Excellent' : backendData.hotelStar >= 3 ? 'Good' : 'Fair',
    address: backendData.address?.completeAddress || 
             [backendData.address?.cityName, backendData.address?.stateName, backendData.address?.countryName]
               .filter(Boolean).join(', '),
    description: backendData.description,
    checkInTime: backendData.checkInTime,
    checkOutTime: backendData.checkOutTime,
    price: transformedRooms.length > 0 ? Math.min(...transformedRooms.map(r => r.roomPrice || 0).filter(p => p > 0)) : null,
    phoneNo: backendData.phoneNo,
    email: backendData.email,
    faxNo: backendData.faxNo,
    hotelWebsiteUrl: backendData.hotelWebsiteUrl,
  };

  // Return transformed structure matching frontend expectations
  return {
    hotelId: backendData.hotelId,
    hotelName: backendData.hotelName,
    provider: backendData.provider,
    searchSessionId: backendData.searchSessionId,
    mainImage: backendData.hotelMainImage,
    // Transform images array: backend sends strings, frontend expects objects with imageUrl
    images: backendData.hotelImages?.map(url => ({ imageUrl: url })) || [],
    overview: overview,
    facilities: backendData.hotelFacilities || [],
    roomDetails: transformedRooms,
    termsAndConditions: backendData.termsAndConditions || [],
    address: backendData.address,
  };
};

export const useHotelDetails = (hotelId, provider, searchId) => {
  const [loading, setLoading] = useState(true);
  const [hotelDetails, setHotelDetails] = useState(null);
  const [isProfileComplete, setIsProfileComplete] = useState(false);
  const [timerKey, setTimerKey] = useState('');
  const [expiryTime, setExpiryTime] = useState(0);
  const [roomWiseCancellationPolicy, setRoomWiseCancellationPolicy] = useState({});
  const [error, setError] = useState(null);
  const [hotelDetailsKey, setHotelDetailsKey] = useState(''); // Redis key for hotelDetails

  const fetchHotelDetails = useCallback(async () => {
    if (!hotelId || !provider || !searchId) {
      setError('Missing required parameters');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      console.log('🏨 Fetching hotel details:', { hotelId, provider, searchId });

      const response = await agentApiService.getHotelDetails(hotelId, provider, searchId);

      if (response.success) {
        // Transform backend response to match frontend structure
        const transformedHotelDetails = transformHotelDetailsResponse(response.hotelDetails);
        
        console.log('🔍 Backend Response:', response.hotelDetails);
        console.log('🔄 Transformed Data:', transformedHotelDetails);
        console.log('👤 Profile Complete Status:', response.isProfileComplete);
        console.log('🔑 Redis Key for hotelDetails:', response.hotelDetailsKey);
        
        setHotelDetails(transformedHotelDetails);
        setIsProfileComplete(response.isProfileComplete);
        setTimerKey(response.timerKey);
        setExpiryTime(response.expiryTime);
        setRoomWiseCancellationPolicy(response.roomWiseCancellationPolicy || {});
        setHotelDetailsKey(response.hotelDetailsKey || ''); // Store Redis key
        
        console.log('✅ Hotel details loaded successfully');
        console.log('📋 State updated - isProfileComplete:', response.isProfileComplete);
      } else {
        throw new Error(response.message || 'Failed to fetch hotel details');
      }
    } catch (error) {
      console.error('❌ Error fetching hotel details:', error);
      setError(error.message);
      toast.error(`Failed to load hotel details: ${error.message}`);
    } finally {
      setLoading(false);
    }
  }, [hotelId, provider, searchId]);

  useEffect(() => {
    fetchHotelDetails();
  }, [fetchHotelDetails]);

  const refetch = useCallback(() => {
    fetchHotelDetails();
  }, [fetchHotelDetails]);

  return {
    loading,
    hotelDetails,
    isProfileComplete,
    timerKey,
    expiryTime,
    roomWiseCancellationPolicy,
    error,
    refetch,
    hotelDetailsKey, // Return Redis key
  };
};